import React, { useState } from 'react'
import { Dialog, DialogContent, IconButton, Tooltip } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import AddressQualityCard from './detail/AddressQualityCard'
import { getMerchant } from '../../helpers/ReduxHelpers'

// NETRA quality band -> color (filled pill). Mirrors the detail-page card's
// success/info/warning/error mapping, as hex for a solid colored badge.
const BAND_COLOR = {
  EXCELLENT: '#2e7d32', // green
  GOOD: '#0288d1', // blue
  FAIR: '#ed6c02', // amber
  POOR: '#d32f2f', // red
}

/**
 * Compact, color-coded NETRA address-quality score for list views.
 * Shows just the score (0–100) in a band-colored pill; band + source on hover.
 * Renders nothing when the order hasn't been scored.
 *
 * When `orderId` is supplied, the pill is clickable and opens a dialog with the
 * full Address Quality card (gauge + sub-score bars + tips) — the same card as
 * the order-detail page, which lazy-fetches its detail from the orderId.
 */
const NetraScoreBadge = ({ netra, orderId, scorePath, scoredStageNote }) => {
  const [open, setOpen] = useState(false)

  // Shown by default; hidden only when the merchant is explicitly opted out.
  if (getMerchant()?.showNetraScoreInMerchantPanel === false) return null
  if (!netra || netra.score === null || netra.score === undefined) return null
  const score = Math.round(Number(netra.score))
  if (Number.isNaN(score)) return null
  const color = BAND_COLOR[netra.band] || '#616161'
  const clickable = Boolean(orderId)
  // Plain-word verdict shown inline next to the score, so the seller reads "Good/Fair/Poor"
  // at a glance without hovering for the tooltip.
  const verdict = { EXCELLENT: 'Excellent', GOOD: 'Good', FAIR: 'Fair', POOR: 'Poor' }[netra.band] || netra.band || '—'

  const handleOpen = (e) => {
    if (!clickable) return
    e.stopPropagation() // don't trigger row/cell selection
    setOpen(true)
  }
  const handleClose = (e) => {
    e?.stopPropagation?.()
    setOpen(false)
  }

  return (
    <>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <Tooltip
        title={
          clickable
            ? `Address quality: ${netra.band || '—'} (${score}/100) · click for details`
            : `Address quality: ${netra.band || '—'} (${score}/100) · NETRA`
        }
        arrow
      >
        <span
          role={clickable ? 'button' : undefined}
          tabIndex={clickable ? 0 : undefined}
          onClick={handleOpen}
          onKeyDown={(e) => {
            if (clickable && (e.key === 'Enter' || e.key === ' ')) handleOpen(e)
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 30,
            height: 22,
            padding: '0 8px',
            borderRadius: 11,
            background: color,
            color: '#fff',
            fontSize: 12,
            fontWeight: 700,
            lineHeight: '22px',
            letterSpacing: 0.2,
            cursor: clickable ? 'pointer' : 'default',
          }}
        >
          <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.4, opacity: 0.9, marginRight: 5 }}>ADDR</span>
          {score}
        </span>
      </Tooltip>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#454F5B', whiteSpace: 'nowrap' }}>{verdict}</span>
      </span>

      {clickable && (
        <Dialog
          open={open}
          onClose={handleClose}
          maxWidth='xs'
          fullWidth
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton
            onClick={handleClose}
            size='small'
            sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}
          >
            <CloseIcon fontSize='small' />
          </IconButton>
          <DialogContent sx={{ p: 2 }}>
            {/* Renders only while the dialog is open, so the netra-score
                fetch fires on demand rather than for every list row. */}
            {open && <AddressQualityCard netra={netra} orderId={orderId} scorePath={scorePath} scoredStageNote={scoredStageNote} />}
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}

export default NetraScoreBadge
